import * as React from 'react';
import { forwardRef, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';

import {
  DiagnosisCustomerSitesCreateDTO,
  DiagnosisCustomerSitesReadDTO,
} from '../../../generated/api-client';
import { useUpdateDiagnosisCustomerSites } from '../../../hooks';
import { siteFormSchema, type SiteFormData } from '../../../schemas/siteFormSchema';
import { SiteDetailForm } from '../../site/SiteDetailForm';
import { DiagnosisStepHandle } from './Step.types';
import { Box } from '@mui/material';
import { formatDateForServer } from '../../../utils/date.utils';

interface DiagnosisSiteStepProps {
  diagnosisSiteId?: string;
  data?: DiagnosisCustomerSitesReadDTO | null;
}

const defaultValues: SiteFormData = {
  customer_id: undefined,
  address: '',
  iso_50001_certified_site: null,
  covered_area: null,
  telecom_network_presence: null,
  cogeneration_availability: null,
  site_framing: '',
  excluded_areas: '',
  norm_unicei_16247_1: true,
  norm_unicei_16247_2: false,
  norm_unicei_16247_3: false,
  norm_unicei_16247_4: false,
  notes: '',
  property: '',
  type: '',
  availability_until: null,
  construction_year: null,
  renovation_year: null,
  energy_performance_certificate: false,
  efficiency_class: '',
  building_envelope_walls_floors: '',
  building_envelope_insulation: '',
  building_envelope_transmittance: '',
  fixtures_glass: '',
  fixtures_frame: '',
  fixtures_transmittance: '',
  fixtures_shading: '',
  img: null,
};

const emptyToUndefined = (value: string | null | undefined) => {
  if (value === undefined || value === null) return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

const toNullableNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const mapDiagnosisSiteToForm = (entity?: DiagnosisCustomerSitesReadDTO | null): SiteFormData => ({
  customer_id: entity?.customer_id ?? undefined,
  address: entity?.address ?? '',
  iso_50001_certified_site: entity?.iso_50001_certified_site ?? null,
  covered_area: entity?.covered_area ?? null,
  telecom_network_presence: entity?.telecom_network_presence ?? null,
  cogeneration_availability: entity?.cogeneration_availability ?? null,
  site_framing: entity?.site_framing ?? '',
  excluded_areas: entity?.excluded_areas ?? '',
  norm_unicei_16247_1: entity?.norm_unicei_16247_1 ?? true,
  norm_unicei_16247_2: entity?.norm_unicei_16247_2 ?? false,
  norm_unicei_16247_3: entity?.norm_unicei_16247_3 ?? false,
  norm_unicei_16247_4: entity?.norm_unicei_16247_4 ?? false,
  notes: entity?.notes ?? '',
  property: entity?.property ?? '',
  type: entity?.type ?? '',
  availability_until: entity?.availability_until ? new Date(entity.availability_until) : null,
  construction_year: entity?.construction_year ? new Date(entity.construction_year) : null,
  renovation_year: entity?.renovation_year ? new Date(entity.renovation_year) : null,
  energy_performance_certificate: entity?.energy_performance_certificate ?? false,
  efficiency_class: entity?.efficiency_class ?? '',
  building_envelope_walls_floors: entity?.building_envelope_walls_floors ?? '',
  building_envelope_insulation: entity?.building_envelope_insulation ?? '',
  building_envelope_transmittance: entity?.building_envelope_transmittance ?? '',
  fixtures_glass: entity?.fixtures_glass ?? '',
  fixtures_frame: entity?.fixtures_frame ?? '',
  fixtures_transmittance: entity?.fixtures_transmittance ?? '',
  fixtures_shading: entity?.fixtures_shading ?? '',
  img: entity?.img ?? null,
});

const mapFormToDiagnosisSiteDTO = (form: SiteFormData): DiagnosisCustomerSitesCreateDTO =>
  new DiagnosisCustomerSitesCreateDTO({
    customer_id: emptyToUndefined(form.customer_id),
    address: form.address,
    iso_50001_certified_site: form.iso_50001_certified_site as boolean,
    covered_area: toNullableNumber(form.covered_area),
    telecom_network_presence: form.telecom_network_presence as boolean,
    cogeneration_availability: form.cogeneration_availability as boolean,
    site_framing: emptyToUndefined(form.site_framing),
    excluded_areas: emptyToUndefined(form.excluded_areas),
    norm_unicei_16247_1: form.norm_unicei_16247_1 ?? true,
    norm_unicei_16247_2: form.norm_unicei_16247_2 ?? false,
    norm_unicei_16247_3: form.norm_unicei_16247_3 ?? false,
    norm_unicei_16247_4: form.norm_unicei_16247_4 ?? false,
    notes: emptyToUndefined(form.notes),
    property: emptyToUndefined(form.property),
    type: emptyToUndefined(form.type),
    availability_until: formatDateForServer(form.availability_until),
    construction_year: formatDateForServer(form.construction_year),
    renovation_year: formatDateForServer(form.renovation_year),
    energy_performance_certificate: form.energy_performance_certificate ?? false,
    efficiency_class: emptyToUndefined(form.efficiency_class),
    building_envelope_walls_floors: emptyToUndefined(form.building_envelope_walls_floors),
    building_envelope_insulation: emptyToUndefined(form.building_envelope_insulation),
    building_envelope_transmittance: emptyToUndefined(form.building_envelope_transmittance),
    fixtures_glass: emptyToUndefined(form.fixtures_glass),
    fixtures_frame: emptyToUndefined(form.fixtures_frame),
    fixtures_transmittance: emptyToUndefined(form.fixtures_transmittance),
    fixtures_shading: emptyToUndefined(form.fixtures_shading),
  });

export const DiagnosisSiteStep = forwardRef<DiagnosisStepHandle, DiagnosisSiteStepProps>(
  ({ diagnosisSiteId, data }, ref) => {
    const formMethods = useForm<SiteFormData>({
      defaultValues,
      resolver: zodResolver(siteFormSchema),
      mode: 'onChange',
    });

    // Watch tutti i campi per forzare il re-render quando cambiano
    formMethods.watch();


    const updateDiagnosisSite = useUpdateDiagnosisCustomerSites();

    useEffect(() => {
      if (data) {
        formMethods.reset(mapDiagnosisSiteToForm(data));
      } else {
        formMethods.reset(defaultValues);
      }
    }, [data, formMethods]);

    const performSave = async (formData: SiteFormData) => {
      if (!diagnosisSiteId) {
        return { success: true };
      }

      try {
        const payload = mapFormToDiagnosisSiteDTO(formData);
        await updateDiagnosisSite.mutateAsync({
          id: diagnosisSiteId,
          data: payload,
        });
        toast.success('Dati sito salvati');
        return { success: true };
      } catch (error: any) {
        const message = error?.message || 'Errore nel salvataggio del sito';
        toast.error(message);
        return { success: false };
      }
    };

    React.useImperativeHandle(ref, () => ({
      save: async (force = false) => {
        const isValid = force ? true : await formMethods.trigger(undefined, { shouldFocus: true });
        if (!isValid && !force) {
          return { success: false, requiresConfirmation: true };
        }
        const values = formMethods.getValues();
        return performSave(values);
      },
    }));

    return (
      <FormProvider {...formMethods}>
        <Box sx={{ p: 2 }}>
          <SiteDetailForm showMap={false} />
        </Box>
      </FormProvider>
    );
  }
);

DiagnosisSiteStep.displayName = 'DiagnosisSiteStep';
